"use client";

import { FC, ReactNode, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { twMerge } from "tailwind-merge";

interface BFLModalProps {
  isOpen: boolean;
  title: string;
  description?: string;
  buttonText?: string;
  subtitle?: string;
  children: ReactNode;
  onClose: () => void;
  enableScroll?: boolean;
  secondaryButtonText?: string;
  onSecondaryButtonClick?: () => void;
  onPrimaryButtonClick?: () => void;
  showHeader?: boolean;
  contentClassName?: string;
  modalClassName?: string;
  footerClassName?: string;
  closeButtonClassName?: string;
  closeIconClassName?: string;
  headerClassName?: string;
  maxWidthClassName?: string;
  bodyClassName?: string;
  contentWrapperClassName?: string;
}

const BFLModal: FC<BFLModalProps> = ({
  isOpen,
  title,
  subtitle,
  description,
  buttonText = "",
  children,
  onClose,
  enableScroll = true,
  secondaryButtonText,
  onSecondaryButtonClick,
  showHeader,
  onPrimaryButtonClick,
  contentClassName,
  bodyClassName,
  modalClassName,
  footerClassName,
  closeButtonClassName,
  closeIconClassName,
  headerClassName,
  maxWidthClassName = "lg:max-w-[512px]",
  contentWrapperClassName
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const el = contentRef.current;

    const handleScroll = () => {
      if (!el) return;
      setIsScrolled(el.scrollTop > 0);
    };

    if (el && enableScroll) {
      el.addEventListener("scroll", handleScroll);
      handleScroll();
    }

    return () => {
      if (el && enableScroll) {
        el.removeEventListener("scroll", handleScroll);
      }
    };
  }, [isOpen, enableScroll]);

  useEffect(() => {
    if (isOpen) {
      document.body.classList.add("overflow-hidden");
    } else {
      document.body.classList.remove("overflow-hidden");
    }

    return () => {
      document.body.classList.remove("overflow-hidden");
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <>
      <button
        className="bg-black-500 fixed inset-0 z-[100000] bg-opacity-50"
        onClick={onClose}
      />

      <div className={twMerge("fixed inset-0 z-[100001] flex items-end justify-center md:items-center lg:px-4", modalClassName)}>
        <div className={twMerge("relative w-full rounded-lg bg-white", maxWidthClassName, contentClassName, bodyClassName)}>
          {showHeader !== false && (
            <div
              className={twMerge(
                "px-4 pb-4 pt-3",
                isScrolled ? "shadow-[0_5px_10px_#0003]" : "",
                headerClassName
              )}
            >
              <button
                onClick={onClose}
                className={twMerge("text-link-primary absolute right-[10px] top-[12px] cursor-pointer text-xs", closeButtonClassName)}
              >
                <i className={twMerge(String.raw`icomoon before:content-['\e914']`, closeIconClassName)}></i>
              </button>
              {title && (
                <h2 className="mt-4 text-center text-lg font-medium leading-[21.33px]">
                  {title}
                </h2>
              )}
              {subtitle && (
                <p className="mt-1 text-center text-xs text-gray-900">
                  {subtitle}
                </p>
              )}
              {description && (
                <p className="mt-2 text-center text-xs">{description}</p>
              )}
            </div>
          )}

          <div
            ref={contentRef}
            className={twMerge(
              "px-4 text-xs md:text-sm",
              enableScroll
                ? `custom-scroll overflow-auto ${contentClassName ?? "max-h-[306px]"}`
                : "",
              contentWrapperClassName
            )}
          >
            {children}
          </div>

          <div
            className={twMerge(
              "w-full pb-4 flex justify-center gap-4 max-[400px]:gap-2",
              footerClassName,
              isScrolled ? "shadow-[-5px_-5px_10px_#0003]" : ""
            )}
          >
            {secondaryButtonText && onSecondaryButtonClick ? (
              <>
                <button
                  onClick={onSecondaryButtonClick}
                  className="mt-4 w-[200px] cursor-pointer rounded-[200px] border border-orange-500 px-[78px] py-[11px] text-sm font-medium uppercase leading-[18px] text-orange-500 transition-all duration-300 ease-in-out hover:bg-orange-50 max-[400px]:w-[165px] max-[400px]:px-4"
                >
                  {secondaryButtonText}
                </button>
                {buttonText && (
                  <button
                    onClick={onPrimaryButtonClick}
                    className="mt-4 w-[200px] cursor-pointer rounded-[200px] bg-gradient-to-r from-link-primary to-[#ff8900] px-[78px] py-[11px] text-sm font-medium uppercase leading-[18px] text-white transition-all duration-300 ease-in-out hover:bg-gradient-to-l hover:from-link-primary hover:to-[#ff8900] max-[400px]:w-[165px] max-[400px]:px-4"
                  >
                    {buttonText}
                  </button>
                )}
              </>
            ) : (
              buttonText && (
                <button
                  onClick={onClose}
                  className="mt-4 w-[200px] cursor-pointer rounded-[200px] bg-gradient-to-r from-[#ff6700] to-[#ff8900] px-[78px] py-[11px] text-sm font-medium uppercase leading-[18px] text-white transition-all duration-300 ease-in-out hover:bg-gradient-to-l hover:from-[#ff6700] hover:to-[#ff8900] max-[400px]:w-[165px] max-[400px]:px-4"
                >
                  {buttonText}
                </button>
              )
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default BFLModal;
